import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Address, OccEndpointsService, TranslationService, UserIdService } from '@spartacus/core';
import { catchError, map, Observable, switchMap } from 'rxjs';
import { failStatus, StatusRequest, successStatus } from '../../../../../core/models/request-status.model';
import {
  AddressRequest,
  CarriersRequest,
  ShippingPreferencesRequest,
  SubDeliveryMethodsRequest,
} from './checkout.model';
import { ShippingPriorityUnformatted } from '../../../../../core/models/cart.model';
import { CustomActiveCartService } from '../../../cart/_core/active-cart.service';

@Injectable({
  providedIn: 'root',
})
export class CheckoutAuxAdapter {
  constructor(
    private http: HttpClient,
    private translationService: TranslationService,
    private occEndPointService: OccEndpointsService,
    private cartService: CustomActiveCartService,
    private userIdService: UserIdService,
  ) {}

  createAddress(address: Address): Observable<AddressRequest> {
    const url = this.occEndPointService.buildUrl('createAddress');

    return this.http.post(url, address).pipe(
      map(response => {
        return {
          ...{
            address: response,
            ...successStatus,
          },
        };
      }),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            ...failStatus,
            message: textError,
          })),
        );
      }),
    );
  }

  getShippingPreferences(): Observable<ShippingPreferencesRequest> {
    return this.cartService.getActiveCartId().pipe(
      switchMap(cartId => {
        const url = this.occEndPointService.buildUrl('getShippingPreferences', {
          urlParams: { cartId },
        });

        return this.http.get(url).pipe(
          map((response: any) => {
            return {
              ...{
                preferences: response || [],
                ...successStatus,
              },
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                preferences: [
                  {
                    code: '123',
                    label: 'Standard',
                  },
                  {
                    code: '321',
                    label: 'Others',
                  },
                ],
                ...successStatus,
                message: textError,
              })),
            );
          }),
        );
      }),
    );
  }

  setShippingPreferences(newPreference: string): Observable<StatusRequest> {
    return this.cartService.getActiveCartId().pipe(
      switchMap(cartId => {
        const url = this.occEndPointService.buildUrl('setShippingPreference', {
          urlParams: { cartId },
        });

        return this.http.post(url, { shippingPreference: newPreference }).pipe(
          map(() => {
            return {
              ...successStatus,
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                ...failStatus,
                message: textError,
              })),
            );
          }),
        );
      }),
    );
  }

  getCarriers(): Observable<CarriersRequest> {
    return this.cartService.getActiveCartId().pipe(
      switchMap(cartId => {
        const url = this.occEndPointService.buildUrl('getCarriers', {
          urlParams: { cartId },
        });

        return this.http.get(url).pipe(
          map((response: any) => {
            return {
              ...{
                carriers: response || [],
                ...successStatus,
              },
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                ...failStatus,
                message: textError,
              })),
            );
          }),
        );
      }),
    );
  }

  getSubDeliveryMethods(): Observable<SubDeliveryMethodsRequest> {
    return this.cartService.getActiveCartId().pipe(
      switchMap(cartId => {
        const url = this.occEndPointService.buildUrl('getSubDeliveryMethods', {
          urlParams: { cartId },
        });

        return this.http.get(url).pipe(
          map((response: any) => {
            return {
              ...{
                methods: response?.deliveryModes || [],
                ...successStatus,
              },
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                ...failStatus,
                message: textError,
              })),
            );
          }),
        );
      }),
    );
  }

  setShippingPriority(form: ShippingPriorityUnformatted): Observable<StatusRequest> {
    return this.cartService.getActiveCartId().pipe(
      switchMap(cartId => {
        const url = this.occEndPointService.buildUrl('setShippingPriority', {
          urlParams: { cartId },
        });

        return this.http.post(url, form).pipe(
          map(() => {
            return {
              ...successStatus,
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                ...failStatus,
                message: textError,
              })),
            );
          }),
        );
      }),
    );
  }
}
